import type { ToastOptions } from 'react-toastify';
import { InfoIcon } from '@rsos/assets/icons';
import {
  displayToast,
  dismissToast,
  updateToast,
  isToastActive,
} from '@rsos/components/capstone/base/Toast';
import ToastBanner from '@rsos/components/capstone/base/Toast/ToastBanner';

/** Identifies which of the two Hermes clients a notice is about. */
export type SocketClient = 'streamWS' | 'hermesConnect';

/**
 * One toast id per client. A shared id would let whichever socket recovered
 * first dismiss the notice while the other was still dead, which is the
 * silence this work exists to remove.
 */
const TOAST_IDS: Record<SocketClient, string> = {
  streamWS: 'socket-connection-stream-ws',
  hermesConnect: 'socket-connection-hermes-connect',
};

const RECONNECTING_MESSAGE = 'Reconnecting to live updates…';

function LiveUpdatesStopped() {
  return (
    <ToastBanner
      dataName="socket-connection-toast"
      icon={<InfoIcon data-name="socket-connection-toast-icon" />}
      buttonText="Reload"
      buttonDataName="socket-connection-toast-reload-button"
      onAction={() => window.location.reload()}
      // This toast has no close button, so a dispatcher trying to get it off
      // the screen would click the body. Everywhere else that opens a
      // conversation; here it would reload the page mid-call.
      onActionOnClick={false}
    >
      Live updates have stopped. Try refreshing the page or logging out and back
      in again.
    </ToastBanner>
  );
}

/**
 * Tells the dispatcher that a socket is down. Without this a dead socket looks
 * exactly like a quiet night, which is the reported symptom behind this work.
 *
 * The notice never auto-closes: the condition it reports outlasts any timeout,
 * and a notice that dismisses itself while updates are still not arriving would
 * recreate the problem. Once the client has given up, the toast also loses its
 * close button, because dismissing it would leave a normal-looking screen
 * showing stale data with no way back.
 */
export function notifySocketDisconnected(
  client: SocketClient,
  { exhausted }: { exhausted: boolean },
): void {
  const toastId = TOAST_IDS[client];
  const message = exhausted ? <LiveUpdatesStopped /> : RECONNECTING_MESSAGE;
  // Both the background and the text color come from this class. Without one
  // the toast falls back to Toastify__overwrite-default, whose text color is
  // set for a light background while `type` repaints the background — the text
  // then renders in the background's own color and the toast looks empty.
  const className = exhausted
    ? 'Toastify__socket-stopped'
    : 'Toastify__socket-reconnecting';
  // Annotated so the ternary is checked against TypeOptions instead of
  // widening to string.
  const options: ToastOptions = {
    toastId,
    type: exhausted ? 'error' : 'warning',
    // `type` makes react-toastify inject its own icon as a second child of the
    // toast body, and the shared `.Toastify__overwrite-body > div` rule sizes
    // every child to 100% — written when the body only ever had one child. The
    // injected icon then fills the toast and squeezes the message to zero
    // width. No other toast in the app sets `type`, so none of them hit this.
    icon: false,
    autoClose: false,
    closeButton: !exhausted,
    closeOnClick: false,
    draggable: false,
  };

  if (isToastActive(toastId)) {
    // A client that exhausts its retries swaps the reconnecting notice for the
    // stopped one in place, so the class has to move with it.
    updateToast(toastId, { ...options, render: message, className });
    return;
  }

  displayToast({ message, options, className });
}

/** Clears the notice for a client once its socket is serving data again. */
export function notifySocketReconnected(client: SocketClient): void {
  dismissToast(TOAST_IDS[client]);
}
